/**
 * Analysis Suite — wrapper page that holds both analysis tools in one place:
 *   - "Underwrite" : pre-purchase deal analysis (For-Sale or Rental)
 *   - "Financial Analysis" : post-acquisition exit / hold modeling on owned projects
 *
 * Route: /analysis-suite?tab=underwrite|project
 * Sidebar entry: "Analysis Suite" (Calculator icon)
 *
 * Both old routes (`/underwrite`, `/financial-analysis`) are preserved as
 * aliases — they redirect into here with the right tab pre-selected so any
 * deep links or bookmarks keep working.
 */
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Info, Calculator, TrendingUp, Building2 } from 'lucide-react';
import DealUnderwriter from './DealUnderwriter';
import FinancialAnalysis from './FinancialAnalysis';
import TourLauncher from '@/components/TourLauncher';

const TABS = [
  {
    id: 'underwrite',
    label: 'Underwrite a Deal',
    icon: Calculator,
    blurb: 'For deals you don\u2019t own yet — model a flip or rental from scratch and decide whether to fund it.',
  },
  {
    id: 'project',
    label: 'Project Analysis',
    icon: TrendingUp,
    blurb: 'For projects already in your portfolio — model sale vs. hold exits using each project\u2019s real numbers.',
  },
];

export default function AnalysisSuite({ user, onLogout }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = params.get('tab') === 'project' ? 'project' : 'underwrite';

  const switchTab = (id) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  };

  // Helpful for keyboard users: scroll to top when switching tabs
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [activeTab]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="analysis-suite-page">
      {/* Suite header + tab switcher */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-0">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-600">DrawReady</p>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                Analysis Suite
              </h1>
              <p className="text-xs text-slate-600 mt-0.5">All your deal math in one place — model new deals before funding, then track them once they're real.</p>
            </div>
            <TourLauncher tourKey="analysis-suite" userId={user?.id} />
          </div>

          {/* Explainer card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex gap-2.5" data-testid="analysis-suite-explainer">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-slate-700 space-y-1">
              <p><strong className="text-blue-700">Underwrite a Deal</strong> — Sizing up a property you don't own yet? Use this to model the full deal from scratch (acquisition + construction + financing + exit). Both for-sale flips and rental holds. Save it as a "Deal", then <em>Convert to Project</em> once you decide to do it.</p>
              <p><strong className="text-emerald-700">Project Analysis</strong> — Already own the project? Use this to model sell-vs-hold exits, monthly carry, and break-even sale price using the project's actual live numbers from your portfolio.</p>
            </div>
          </div>

          {/* Tab buttons */}
          <div className="flex gap-1 -mb-px" role="tablist">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = t.id === activeTab;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => switchTab(t.id)}
                  className={`group relative px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-700 bg-blue-50/40'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                  }`}
                  data-testid={`analysis-tab-${t.id}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {t.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active tool */}
      <div className="pt-2" data-testid={`analysis-active-${activeTab}`}>
        {activeTab === 'underwrite' ? (
          <DealUnderwriter user={user} />
        ) : (
          <FinancialAnalysis user={user} onLogout={onLogout} />
        )}
      </div>
    </div>
  );
}
